import { useState } from 'react';
import uuid from 'uuid/v4';
import {
  useJ1AuthFetch,
  urls,
  SynchronizationJobResponse,
  SynchronizationJobStart,
  useSnackbarNotification,
  SynchronizationJobStatus,
} from '@jupiterone/jupiter-web-toolkit/src';

interface BulkUploadArgs {
  dataFile: File;
  scope?: string;
}

const useBulkSynchronizationUpload = () => {
  const { authFetch, unauthFetch } = useJ1AuthFetch();
  const notifications = useSnackbarNotification();
  const [status, setStatus] = useState<
    SynchronizationJobResponse | undefined
  >();
  const bulkUpload = async ({ dataFile, scope = uuid() }: BulkUploadArgs) => {
    // The synchronization API uses a 3 step process to bulk upload entities/relationships,
    // which includes:
    //    1.  Starting a synchronization job with a scope, and receiving a job id
    //    2.  Request a pre-signed upload URL
    //    3.  Upload file to pre-signed URL
    //    4.  Finalizing the job
    //
    // If these are not executed in order, then the upload process will not be successful.
    setStatus(undefined);
    try {
      // Step 1 - Start the sync job
      const synchronizationJobStart: SynchronizationJobStart = {
        source: 'api',
        scope: scope,
      };
      const startResponse = await authFetch(urls.synchronizationJobs, {
        method: 'POST',
        body: JSON.stringify(synchronizationJobStart),
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!startResponse.ok) return;
      const startStatus = await startResponse.json();
      setStatus(startStatus);
      if (!startStatus.job.id) return;

      // Step 2 - Get pre-signed URL
      const uploadURLResponse = await authFetch(
        `${urls.synchronizationJobs}/${startStatus.job.id}/uploadUrl`,
        {
          method: 'POST',
        }
      );

      if (!uploadURLResponse.ok) return;
      const uploadURLResponseJSON = await uploadURLResponse.json();
      const signedUploadURL = uploadURLResponseJSON.uploadUrl;

      // Step 3 - Upload the data to signed URL
      // We don't want to use an authorized fetch here because the
      // signed url has it's own auth params
      const uploadStatusResponse = await unauthFetch(signedUploadURL, {
        method: 'PUT',
        body: dataFile,
      });

      if (!uploadStatusResponse.ok) return;

      // Step 4 - Finalize the sync job
      const finalStatusResponse = await authFetch(
        `${urls.synchronizationJobs}/${startStatus.job.id}/finalize`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!finalStatusResponse.ok) return;
      const finalStatus = await finalStatusResponse.json();
      setStatus(finalStatus);
      switch (finalStatus.job.status) {
        case SynchronizationJobStatus.FINISHED:
          notifications.success('Upload of asset data was successful.');
          break;
        case SynchronizationJobStatus.FINALIZING_ENTITIES:
        case SynchronizationJobStatus.FINALIZING_RELATIONSHIPS:
        case SynchronizationJobStatus.FINALIZE_PENDING:
          notifications.success('JupiterOne is processing your assets.');
          break;
        case SynchronizationJobStatus.ABORTED:
        case SynchronizationJobStatus.UNKNOWN:
        case SynchronizationJobStatus.ERROR_BAD_DATA:
        case SynchronizationJobStatus.ERROR_UNEXPECTED_FAILURE:
          notifications.warning(
            'Failed to upload entity and relationship data'
          );
      }
    } catch (e) {
      // authFetch will handle the error message
      setStatus(undefined);
    }
  };

  return {
    status,
    bulkUpload,
  };
};

export default useBulkSynchronizationUpload;
